/**
 * ForecastControlsFAB - Floating Action Button toolbar for forecast controls
 *
 * A floating toolbar positioned at the bottom of the viewport that provides:
 * - Cumulative/Month-by-Month toggle (always visible)
 * - Filter button with popover containing all filter controls
 * - Active filter count badge
 *
 * Used by RebatesAnalyticsPage and StatusTabView to provide consistent
 * filter/display controls across rebate forecast views.
 */

import React, { useState, useMemo } from 'react';
import {
  SlidersHorizontalIcon,
  XIcon,
  EyeIcon,
  GearIcon,
  CaretLeftIcon,
  ArrowCounterClockwiseIcon,
} from '@phosphor-icons/react';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { FilterPanelContent, type FilterGroup } from './FilterPanelContent';
import { LineType, LINE_TYPE_LABELS } from '@/types/rebateAnalytics';
import colors from 'src/assets/md-theme/base/colors';
import {
  ALL_PREDICTION_ALGORITHMS,
  PREDICTION_ALGORITHMS,
  PredictionAlgorithm,
} from './ForecastCard';

export interface ForecastControlsFABProps {
  /** Whether the chart displays cumulative data */
  isCumulative: boolean;
  /** Handler for cumulative toggle changes */
  onCumulativeChange: (value: boolean) => void;
  /** Filter group configurations */
  filterGroups: FilterGroup[];
  /** Number of active filters */
  activeFilterCount: number;
  /** Handler to clear all filters */
  onClearAll: () => void;
  /** Position of the FAB */
  position?: 'bottom-center' | 'bottom-right';
  /** Additional class names */
  className?: string;
  /** Set of visible line types */
  visibleLines: Set<string>;
  /** Handler for visibility changes */
  onVisibleLinesChange: (lines: Set<string>) => void;
  /** Currently selected primary line */
  primaryLine: string;
  /** Handler for primary line changes */
  onPrimaryLineChange: (line: string) => void;
  /** Whether previous year data is available */
  hasPreviousYearData?: boolean;
  /** Handler to reset all controls to defaults */
  onReset: () => void;
  /** Whether reset is available (if false, button will be disabled) */
  isResetAvailable: boolean;
  /** Currently selected year (used to disable predictions and reported for 2024 and earlier) */
  selectedYear: number | null;
  /** Available prediction algorithms from the data */
  availablePredictionAlgorithms?: PredictionAlgorithm[];
}

export function ForecastControlsFAB({
  isCumulative,
  onCumulativeChange,
  filterGroups,
  activeFilterCount,
  onClearAll,
  position = 'bottom-center',
  className,
  visibleLines,
  onVisibleLinesChange,
  primaryLine,
  onPrimaryLineChange,
  hasPreviousYearData = false,
  onReset,
  isResetAvailable,
  selectedYear,
  availablePredictionAlgorithms: availablePredictionAlgorithmsProp = [],
}: ForecastControlsFABProps) {
  // Ensure we always have at least linear-regression as a fallback
  const availablePredictionAlgorithms =
    availablePredictionAlgorithmsProp.length > 0
      ? availablePredictionAlgorithmsProp
      : (['linear-regression'] as PredictionAlgorithm[]);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isLinesOpen, setIsLinesOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  // Get color for each line type (for non-split lines)
  const getLineColor = (lineType: LineType): string => {
    switch (lineType) {
      case 'estimates':
        return colors.success.main;
      case 'predictions':
        return colors.info.main; // Blue for predictions
      case 'reported':
        return colors.black.main;
      case 'previousYear':
        return colors.grey?.[400] || '#9CA3AF';
      default:
        return '#6B7280';
    }
  };

  // Check if a line type should have split colors
  const hasSplitColors = (lineType: LineType): boolean => {
    return lineType === 'predictions' || lineType === 'reported';
  };

  // Get split colors for a line type
  const getSplitColors = (
    lineType: LineType,
  ): { left: string; right: string } => {
    if (lineType === 'predictions') {
      return {
        left: '#9C27B0', // Magenta
        right: colors.info.main, // Blue
      };
    }
    if (lineType === 'reported') {
      return {
        left: colors.black.main, // Black
        right: colors.info.main, // Blue
      };
    }
    return { left: '#6B7280', right: '#6B7280' };
  };

  const positionClasses = {
    'bottom-center': 'bottom-6 left-1/2 -translate-x-1/2',
    'bottom-right': 'bottom-6 right-6',
  };

  // Calculate default visible lines (all available lines)
  const defaultVisibleLines = useMemo(() => {
    const defaultSet = new Set([
      'estimates',
      ...ALL_PREDICTION_ALGORITHMS,
      'reported',
    ]);
    if (hasPreviousYearData) {
      defaultSet.add('previousYear');
    }
    return defaultSet;
  }, [hasPreviousYearData]);

  // Count visible lines that are in the default set
  const visibleLineCount = useMemo(() => {
    let count = 0;
    defaultVisibleLines.forEach((line) => {
      if (visibleLines.has(line)) {
        count++;
      }
    });
    return count;
  }, [visibleLines, defaultVisibleLines]);

  // Check if current state differs from default
  const isNonDefault = useMemo(() => {
    console.log({
      visibleLineCount,
      defaultVisibleLinesSize: defaultVisibleLines.size,
    });
    return visibleLineCount !== defaultVisibleLines.size;
  }, [visibleLineCount, defaultVisibleLines.size]);

  return (
    <div
      className={cn(
        'fixed z-[1200] flex items-center gap-3 rounded-full bg-background/95 backdrop-blur-sm border shadow-lg transition-all duration-200',
        isExpanded ? 'px-4 py-2' : 'p-2',
        positionClasses[position],
        className,
      )}
    >
      {isExpanded ? (
        <>
          {/* Cumulative Toggle */}
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'text-xs whitespace-nowrap',
                !isCumulative
                  ? 'text-foreground font-medium'
                  : 'text-muted-foreground',
              )}
            >
              Monthly
            </span>
            <Switch
              checked={isCumulative}
              onCheckedChange={onCumulativeChange}
              className="data-[state=checked]:bg-primary"
            />
            <span
              className={cn(
                'text-xs whitespace-nowrap',
                isCumulative
                  ? 'text-foreground font-medium'
                  : 'text-muted-foreground',
              )}
            >
              Cumulative
            </span>
          </div>

          <div className="h-6 w-px shrink-0 border-l border-border" />

          {/* Line Visibility & Primary Selection */}
          <Popover open={isLinesOpen} onOpenChange={setIsLinesOpen}>
            <PopoverTrigger asChild>
              <Button
                variant={isNonDefault ? 'default' : 'ghost'}
                size="sm"
                className={cn(
                  'relative flex items-center gap-2 h-8 px-3',
                  isNonDefault
                    ? 'bg-purple-600 hover:bg-purple-700 text-white'
                    : 'hover:bg-accent',
                )}
              >
                <EyeIcon className="h-4 w-4 mr-2" />
                <span className="text-sm">Lines</span>
                {isNonDefault && (
                  <span className="absolute -top-2 -right-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-purple-900 px-1.5 text-xs font-medium text-white ring-2 ring-background">
                    {visibleLineCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              side="top"
              align="center"
              className="w-auto min-w-[400px] max-w-[90vw] p-0"
              sideOffset={12}
            >
              <div className="p-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2">Line</th>
                      <th className="text-center py-2 px-2">Chart</th>
                      <th className="text-center py-2 px-2">Tables</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Estimates row */}
                    {(() => {
                      const isVisible = visibleLines.has('estimates');
                      const isPrimary = primaryLine === 'estimates';

                      const handleVisibilityToggle = (checked: boolean) => {
                        const newVisible = new Set(visibleLines);
                        if (
                          !checked &&
                          visibleLines.size === 1 &&
                          visibleLines.has('estimates')
                        ) {
                          return;
                        }
                        if (checked) {
                          newVisible.add('estimates');
                        } else {
                          newVisible.delete('estimates');
                          if (primaryLine === 'estimates') {
                            const remainingVisible = Array.from(newVisible);
                            if (remainingVisible.length > 0) {
                              onPrimaryLineChange(remainingVisible[0]);
                            }
                          }
                        }
                        onVisibleLinesChange(newVisible);
                      };

                      return (
                        <tr className="border-b">
                          <td
                            className={cn(
                              'py-2 px-2',
                              !isVisible && 'opacity-50',
                            )}
                          >
                            <div className="flex items-center gap-2">
                              <div
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{ backgroundColor: colors.success.main }}
                              />
                              <span
                                className={cn(
                                  !isVisible && 'text-muted-foreground',
                                )}
                              >
                                {LINE_TYPE_LABELS.estimates}
                              </span>
                            </div>
                          </td>
                          <td className="text-center py-2 px-2">
                            <Checkbox
                              checked={isVisible}
                              onCheckedChange={handleVisibilityToggle}
                            />
                          </td>
                          <td className="text-center py-2 px-2">
                            <input
                              type="radio"
                              name="primary-line"
                              checked={isPrimary}
                              disabled={!isVisible}
                              onChange={() => onPrimaryLineChange('estimates')}
                              className="cursor-pointer"
                            />
                          </td>
                        </tr>
                      );
                    })()}

                    {/* Prediction Algorithm rows - one for each algorithm */}
                    {/* Predictions are gated behind estimates - disabled when estimates is OFF */}
                    {availablePredictionAlgorithms.map((algorithm) => {
                      const algorithmInfo = PREDICTION_ALGORITHMS[algorithm];
                      // Check if this algorithm is in visibleLines
                      const isVisible = visibleLines.has(algorithm);
                      // Each algorithm has its own primary selection
                      const isPrimary = primaryLine === algorithm;
                      const isHistoricalYear =
                        selectedYear !== null && selectedYear <= 2024;
                      // Predictions are gated behind estimates
                      const isEstimatesVisible = visibleLines.has('estimates');
                      const isGated = !isEstimatesVisible;

                      const handleAlgorithmToggle = (checked: boolean) => {
                        // Don't allow toggling if gated (estimates is OFF)
                        if (isGated) return;

                        const newVisible = new Set(visibleLines);

                        // Prevent unchecking if it's the last visible line
                        if (
                          !checked &&
                          visibleLines.size === 1 &&
                          visibleLines.has(algorithm)
                        ) {
                          return;
                        }

                        if (checked) {
                          newVisible.add(algorithm);
                        } else {
                          newVisible.delete(algorithm);
                        }

                        onVisibleLinesChange(newVisible);
                      };

                      // Effective visibility: algorithm is visible AND estimates is visible
                      const isEffectivelyVisible =
                        isVisible && isEstimatesVisible;

                      return (
                        <tr key={algorithm} className="border-b">
                          <td
                            className={cn(
                              'py-2 px-2 pl-6', // Indent to show hierarchy under estimates
                              (isGated || !isVisible) &&
                                !isHistoricalYear &&
                                'opacity-50',
                            )}
                          >
                            <div className="flex items-center gap-2">
                              <div
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{
                                  backgroundColor: isGated
                                    ? '#9CA3AF'
                                    : algorithmInfo.color,
                                }}
                              />
                              <span
                                className={cn(
                                  (isGated || !isVisible) &&
                                    !isHistoricalYear &&
                                    'text-muted-foreground',
                                )}
                              >
                                {algorithmInfo.label}
                              </span>
                              {isHistoricalYear ? (
                                <span className="text-xs text-muted-foreground ml-2">
                                  (unavailable)
                                </span>
                              ) : isGated ? (
                                <span className="text-xs text-muted-foreground ml-2">
                                  (requires Estimates)
                                </span>
                              ) : null}
                            </div>
                          </td>
                          <td className="text-center py-2 px-2">
                            <Checkbox
                              checked={isVisible && !isHistoricalYear}
                              onCheckedChange={handleAlgorithmToggle}
                              disabled={isHistoricalYear || isGated}
                            />
                          </td>
                          <td className="text-center py-2 px-2">
                            <input
                              type="radio"
                              name="primary-line"
                              checked={isPrimary && isEffectivelyVisible}
                              disabled={
                                !isEffectivelyVisible || isHistoricalYear
                              }
                              onChange={() => onPrimaryLineChange(algorithm)}
                              className="cursor-pointer"
                            />
                          </td>
                        </tr>
                      );
                    })}

                    {/* Reported row */}
                    {(() => {
                      const isVisible = visibleLines.has('reported');
                      const isPrimary = primaryLine === 'reported';
                      const isHistoricalYear =
                        selectedYear !== null && selectedYear <= 2024;

                      const handleVisibilityToggle = (checked: boolean) => {
                        const newVisible = new Set(visibleLines);
                        if (
                          !checked &&
                          visibleLines.size === 1 &&
                          visibleLines.has('reported')
                        ) {
                          return;
                        }
                        if (checked) {
                          newVisible.add('reported');
                        } else {
                          newVisible.delete('reported');
                          if (primaryLine === 'reported') {
                            const remainingVisible = Array.from(newVisible);
                            if (remainingVisible.length > 0) {
                              onPrimaryLineChange(remainingVisible[0]);
                            }
                          }
                        }
                        onVisibleLinesChange(newVisible);
                      };

                      return (
                        <tr className="border-b">
                          <td
                            className={cn(
                              'py-2 px-2',
                              !isVisible && !isHistoricalYear && 'opacity-50',
                            )}
                          >
                            <div className="flex items-center gap-2">
                              <div
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{ backgroundColor: colors.black.main }}
                              />
                              <span
                                className={cn(
                                  !isVisible &&
                                    !isHistoricalYear &&
                                    'text-muted-foreground',
                                )}
                              >
                                {LINE_TYPE_LABELS.reported}
                              </span>
                              {isHistoricalYear && (
                                <span className="text-xs text-muted-foreground ml-2">
                                  (unavailable)
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="text-center py-2 px-2">
                            <Checkbox
                              checked={isVisible && !isHistoricalYear}
                              onCheckedChange={handleVisibilityToggle}
                              disabled={isHistoricalYear}
                            />
                          </td>
                          <td className="text-center py-2 px-2">
                            <input
                              type="radio"
                              name="primary-line"
                              checked={isPrimary}
                              disabled={!isVisible || isHistoricalYear}
                              onChange={() => onPrimaryLineChange('reported')}
                              className="cursor-pointer"
                            />
                          </td>
                        </tr>
                      );
                    })()}

                    {/* Previous Year row - only show if data available */}
                    {hasPreviousYearData &&
                      (() => {
                        const isVisible = visibleLines.has('previousYear');
                        const isPrimary = primaryLine === 'previousYear';

                        const handleVisibilityToggle = (checked: boolean) => {
                          const newVisible = new Set(visibleLines);
                          if (
                            !checked &&
                            visibleLines.size === 1 &&
                            visibleLines.has('previousYear')
                          ) {
                            return;
                          }
                          if (checked) {
                            newVisible.add('previousYear');
                          } else {
                            newVisible.delete('previousYear');
                            if (primaryLine === 'previousYear') {
                              const remainingVisible = Array.from(newVisible);
                              if (remainingVisible.length > 0) {
                                onPrimaryLineChange(remainingVisible[0]);
                              }
                            }
                          }
                          onVisibleLinesChange(newVisible);
                        };

                        return (
                          <tr className="border-b">
                            <td
                              className={cn(
                                'py-2 px-2',
                                !isVisible && 'opacity-50',
                              )}
                            >
                              <div className="flex items-center gap-2">
                                <div
                                  className="w-3 h-3 rounded-full flex-shrink-0"
                                  style={{
                                    backgroundColor:
                                      colors.grey?.[400] || '#9CA3AF',
                                  }}
                                />
                                <span
                                  className={cn(
                                    !isVisible && 'text-muted-foreground',
                                  )}
                                >
                                  {LINE_TYPE_LABELS.previousYear}
                                </span>
                              </div>
                            </td>
                            <td className="text-center py-2 px-2">
                              <Checkbox
                                checked={isVisible}
                                onCheckedChange={handleVisibilityToggle}
                              />
                            </td>
                            <td className="text-center py-2 px-2">
                              <input
                                type="radio"
                                name="primary-line"
                                checked={isPrimary}
                                disabled={!isVisible}
                                onChange={() =>
                                  onPrimaryLineChange('previousYear')
                                }
                                className="cursor-pointer"
                              />
                            </td>
                          </tr>
                        );
                      })()}
                  </tbody>
                </table>

                <p className="text-xs text-muted-foreground mt-3">
                  Predictions require Estimates to be visible. Primary line
                  determines current year table data.
                </p>
              </div>
            </PopoverContent>
          </Popover>

          <div className="h-6 w-px shrink-0 border-l border-border" />

          {/* Filter Button with Popover */}
          <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
            <PopoverTrigger asChild>
              <Button
                variant={activeFilterCount > 0 ? 'default' : 'ghost'}
                size="sm"
                className={cn(
                  'relative flex items-center gap-2 h-8 px-3',
                  activeFilterCount > 0
                    ? 'bg-purple-600 hover:bg-purple-700 text-white'
                    : 'hover:bg-accent',
                )}
              >
                <SlidersHorizontalIcon className="h-4 w-4" />
                <span className="text-sm">Filters</span>
                {activeFilterCount > 0 && (
                  <span className="absolute -top-2 -right-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-purple-900 px-1.5 text-xs font-medium text-white ring-2 ring-background">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              side="top"
              align="center"
              className="w-auto min-w-[400px] max-w-[90vw] p-0"
              sideOffset={12}
            >
              <div className="p-4">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-sm">Filters</h3>
                  {activeFilterCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onClearAll}
                      className="h-7 text-xs text-muted-foreground hover:text-foreground"
                    >
                      <XIcon className="h-3 w-3 mr-1" />
                      Clear all
                    </Button>
                  )}
                </div>

                {/* Filter Controls */}
                <FilterPanelContent filterGroups={filterGroups} />
              </div>
            </PopoverContent>
          </Popover>

          {/* Reset Button - Always visible, disabled when at defaults */}
          <div className="h-6 w-px shrink-0 border-l border-border" />
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            disabled={!isResetAvailable}
            className="h-8 w-8 p-0"
            title={
              isResetAvailable
                ? 'Reset all controls to defaults'
                : 'All controls are at default values'
            }
          >
            <ArrowCounterClockwiseIcon className="h-4 w-4" />
          </Button>

          {/* Collapse Button */}
          <div className="h-6 w-px shrink-0 border-l border-border" />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(false)}
            className="h-8 w-8 p-0"
            title="Collapse controls"
          >
            <CaretLeftIcon className="h-4 w-4" />
          </Button>
        </>
      ) : (
        /* Collapsed State - Gear Icon */
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(true)}
          className="h-8 w-8 p-0"
          title="Expand controls"
        >
          <GearIcon className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

export default ForecastControlsFAB;
